/**
 * RestroomSurvey - Guest-facing Review Survey
 * 
 * PUBLIC page - No login required.
 * Supports 0-5 questions (all rating type).
 * If 0 questions: Shows only a free-text comment box.
 * Visual Theme: BLUE (distinguishes from Room Survey which is Green)
 * Full bilingual support (EN/FR).
 */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Star, Loader2, CheckCircle, Globe, MessageSquare, Droplets, Camera, X, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import SurveyLandingPage from './SurveyLandingPage';
import BrandingShell from '../../components/shared/BrandingShell';
import { hasWallpaper } from '../../components/shared/cardSurface';
import GlassInteractionPanel from '../../components/shared/GlassInteractionPanel';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = `${API_BASE}/api/hotels-standalone`;

export default function RestroomSurvey() {
  const { t, i18n } = useTranslation();
  const { orgId, locationId } = useParams();
  const [searchParams] = useSearchParams();
  const cardId = searchParams.get('card');
  const langParam = searchParams.get('lang');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [surveyData, setSurveyData] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState([]);
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [branding, setBranding] = useState({ logo_url: null, logo_size: 'medium', wallpaper_url: null, card_style: 'frosted' });
  const [photo, setPhoto] = useState(null);
  const [photoCompressing, setPhotoCompressing] = useState(false);
  const fileInputRef = useRef(null);
  const [showLandingPage, setShowLandingPage] = useState(false);
  const [languageSettings, setLanguageSettings] = useState(null);
  const [reviewRedirect, setReviewRedirect] = useState(null);
  const [showReviewPrompt, setShowReviewPrompt] = useState(false);

  // Compress image client-side to ~100KB
  const compressImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_DIM = 800;
          let w = img.width, h = img.height;
          if (w > MAX_DIM || h > MAX_DIM) {
            const ratio = Math.min(MAX_DIM / w, MAX_DIM / h);
            w = Math.round(w * ratio);
            h = Math.round(h * ratio);
          }
          canvas.width = w;
          canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL('image/jpeg', 0.6));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoCompressing(true);
    try {
      const dataUrl = await compressImage(file);
      setPhoto({ dataUrl, name: file.name });
    } catch {
      // silently fail
    }
    setPhotoCompressing(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Logo size mapping
  const logoSizeClasses = {
    small: 'h-12 max-w-[100px]',
    medium: 'h-20 max-w-[160px]',
    large: 'h-28 max-w-[220px]',
    very_large: 'h-36 max-w-[280px]'
  };

  // Force dark mode for standalone pages
  useEffect(() => {
    document.documentElement.classList.add('dark');
    return () => {
      const savedTheme = localStorage.getItem('yul_theme_preference');
      if (savedTheme !== 'dark') {
        document.documentElement.classList.remove('dark');
      }
    };
  }, []);

  // Set language from URL param if provided
  useEffect(() => {
    if (langParam && ['en', 'fr'].includes(langParam)) {
      i18n.changeLanguage(langParam);
    }
  }, [langParam, i18n]);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'fr' : 'en';
    i18n.changeLanguage(newLang);
  };

  useEffect(() => {
    fetchSurvey();
  }, [orgId, locationId]);

  const fetchSurvey = async () => {
    try {
      const url = `${API_URL}/restroom-survey/${orgId}/${locationId}?lang=${i18n.language}${cardId ? `&card=${cardId}` : ''}`;
      const response = await fetch(url);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Error loading survey');
      }

      setSurveyData(data);
      if (data.branding) setBranding(data.branding);
      
      // Handle language settings
      const ls = data.language_settings || {};
      setLanguageSettings(ls);
      
      // Handle review redirect settings
      if (data.review_redirect) {
        setReviewRedirect(data.review_redirect);
      }
      
      if (ls.landing_page_enabled && !langParam) {
        setShowLandingPage(true);
      } else if (!langParam && ls.default_lang && ls.default_lang !== i18n.language) {
        i18n.changeLanguage(ls.default_lang);
      }
      
      // If 0 questions, go directly to notes
      if (data.questions.length === 0) {
        setShowNotes(true);
        setResponses([]);
      } else {
        setResponses(new Array(data.questions.length).fill(null));
      }
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };
  
  const handleLandingLanguageSelect = (lang) => {
    i18n.changeLanguage(lang);
    setShowLandingPage(false);
    setLoading(true);
    const url = `${API_URL}/restroom-survey/${orgId}/${locationId}?lang=${lang}${cardId ? `&card=${cardId}` : ''}`;
    fetch(url).then(r => r.json()).then(data => {
      if (data.questions !== undefined) {
        setSurveyData(data);
        if (data.branding) setBranding(data.branding);
        if (data.questions.length === 0) {
          setShowNotes(true);
          setResponses([]);
        } else {
          setResponses(new Array(data.questions.length).fill(null));
        }
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  const handleRatingResponse = (rating) => {
    const newResponses = [...responses];
    newResponses[currentQuestion] = {
      question_id: surveyData.questions[currentQuestion].id,
      response_type: 'rating',
      value: rating
    };
    setResponses(newResponses);
    
    // Auto-advance after selection
    setTimeout(() => {
      if (currentQuestion < surveyData.questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      }
    }, 300);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        card_id: cardId,
        responses: responses.filter(r => r !== null),
        notes: notes.trim() || null
      };
      if (photo?.dataUrl) {
        payload.photo = photo.dataUrl;
      }
      const response = await fetch(`${API_URL}/restroom-survey/${orgId}/${locationId}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Error submitting review');
      }

      // Check if all rating responses are 4+ stars for review redirect
      const ratingResponses = responses.filter(r => r && r.response_type === 'rating');
      const allHighRatings = ratingResponses.length > 0 && ratingResponses.every(r => r.value >= 4);
      
      if (allHighRatings && reviewRedirect?.enabled && reviewRedirect?.url) {
        setShowReviewPrompt(true);
      } else {
        setSubmitted(true);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <BrandingShell wallpaperUrl={branding?.wallpaper_url} fallbackGradient="from-slate-950 to-slate-900">
        <GlassInteractionPanel>
          <div className="p-6 text-center">
            <Loader2 className="w-12 h-12 text-white animate-spin mx-auto mb-4" />
            <p className="text-white text-lg">{t('common.loading')}</p>
          </div>
        </GlassInteractionPanel>
      </BrandingShell>
    );
  }

  // Show language landing page if enabled
  if (showLandingPage && languageSettings) {
    return (
      <SurveyLandingPage
        branding={branding}
        languageSettings={languageSettings}
        organizationName={surveyData?.organization?.name}
        onSelectLanguage={handleLandingLanguageSelect}
        apiBase={API_BASE}
      />
    );
  }

  if (error) {
    return (
      <BrandingShell wallpaperUrl={branding?.wallpaper_url} fallbackGradient="from-slate-950 to-slate-900">
        <GlassInteractionPanel>
          <div className="p-6 text-center">
            <p className="text-red-400 text-lg font-medium mb-2">{t('common.error')}</p>
            <p className="text-white/60">{error}</p>
          </div>
        </GlassInteractionPanel>
      </BrandingShell>
    );
  }

  // Show review redirect prompt for high ratings
  if (showReviewPrompt) {
    return (
      <BrandingShell wallpaperUrl={branding?.wallpaper_url} fallbackGradient="from-slate-950 to-slate-900">
        <GlassInteractionPanel>
          <div className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
              <Star className="w-10 h-10 text-amber-500 fill-amber-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {i18n.language === 'fr' ? 'Merci pour votre avis!' : 'Thank you for your feedback!'}
            </h1>
            <p className="text-white/60 mb-6">
              {i18n.language === 'fr' 
                ? 'Pourriez-vous nous laisser un avis sur Google?'
                : 'Would you kindly leave us a review on Google?'}
            </p>
            <div className="space-y-3">
              <Button
                className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3"
                onClick={() => window.open(reviewRedirect.url, '_blank')}
                data-testid="leave-review-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                {i18n.language === 'fr' ? 'Laisser un avis' : 'Leave a Review'}
              </Button>
              <Button
                variant="ghost"
                className="w-full text-white/50 hover:text-gray-700"
                onClick={() => { setShowReviewPrompt(false); setSubmitted(true); }}
                data-testid="no-thanks-btn"
              >
                {i18n.language === 'fr' ? 'Non, merci' : 'No, thanks'}
              </Button>
            </div>
          </div>
        </GlassInteractionPanel>
      </BrandingShell>
    );
  }

  if (submitted) {
    return (
      <BrandingShell wallpaperUrl={branding?.wallpaper_url} fallbackGradient="from-slate-950 to-slate-900">
        <GlassInteractionPanel>
          <div className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {i18n.language === 'fr' ? 'Merci!' : 'Thank You!'}
            </h1>
            <p className="text-white/60 mb-2">{surveyData?.organization?.name}</p>
            <p className="text-white/50 text-sm">
              {i18n.language === 'fr' 
                ? 'Votre avis nous aide à améliorer nos installations.' 
                : 'Your review helps us improve our services.'}
            </p>
          </div>
        </GlassInteractionPanel>
      </BrandingShell>
    );
  }

  // If no questions, show only the comment form
  if (surveyData?.questions?.length === 0 || showNotes) {
    return (
      <BrandingShell wallpaperUrl={branding?.wallpaper_url} fallbackGradient="from-slate-950 to-slate-900">
        {/* Language Toggle */}
        <div className="absolute top-4 right-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleLanguage}
            className="text-white/80 hover:text-white hover:bg-white/10"
          >
            <Globe className="w-4 h-4 mr-2" />
            {i18n.language === 'en' ? 'FR' : 'EN'}
          </Button>
        </div>

        <GlassInteractionPanel>
          <div className="p-6">
            {/* Header with Logo */}
            <div className="text-center mb-6">
              {branding.logo_url ? (
                <div className="flex justify-center mb-4">
                  <img 
                    src={branding.logo_url.startsWith('http') ? branding.logo_url : `${API_BASE}${branding.logo_url}`}
                    alt={surveyData?.organization?.name || 'Logo'}
                    className={`object-contain ${logoSizeClasses[branding.logo_size] || logoSizeClasses.medium}`}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              ) : (
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                  <Droplets className="w-8 h-8 text-blue-600" />
                </div>
              )}
              <h1 className="text-xl font-bold text-white">{surveyData?.organization?.name}</h1>
              <p className="text-sm text-white/50">
                {i18n.language === 'fr' ? 'Avis' : 'Review'}
              </p>
            </div>

            {/* Comments Form */}
            <div className="mb-6">
              <div className="flex items-center mb-3">
                <MessageSquare className="w-5 h-5 text-blue-600 mr-2" />
                <h2 className="text-lg font-medium text-white">
                  {i18n.language === 'fr' ? 'Votre avis' : 'Your Review'}
                </h2>
              </div>
              <p className="text-sm text-white/50 mb-3">
                {i18n.language === 'fr' 
                  ? 'Partagez vos commentaires ou suggestions concernant nos installations.' 
                  : 'Share your comments or suggestions about our facilities.'}
              </p>
              <Textarea
                placeholder={i18n.language === 'fr' 
                  ? 'Entrez vos commentaires ici...' 
                  : 'Enter your comments here...'}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="min-h-[120px] bg-white/5 border-white/10 text-white placeholder:text-white/30"
                data-testid="restroom-feedback-textarea"
              />

              {/* Optional Photo Upload */}
              <div className="mt-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  className="hidden"
                  data-testid="photo-file-input"
                />
                {photo ? (
                  <div className="relative inline-block">
                    <img src={photo.dataUrl} alt="Attached" className="h-24 rounded-lg border border-gray-200 object-cover" />
                    <button
                      onClick={() => setPhoto(null)}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow"
                      data-testid="remove-photo-btn"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={photoCompressing}
                    className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-2xl bg-white/10 border border-white/25 text-white/90 text-sm font-medium hover:bg-white/15 hover:border-white/35 transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="attach-photo-btn"
                  >
                    {photoCompressing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Camera className="w-4 h-4 mr-2" />
                    )}
                    {i18n.language === 'fr' ? 'Joindre une photo (optionnel)' : 'Attach a photo (optional)'}
                  </button>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-between">
              {surveyData?.questions?.length > 0 && (
                <Button
                  variant="outline"
                  onClick={() => setShowNotes(false)}
                >
                  {i18n.language === 'fr' ? 'Retour' : 'Back'}
                </Button>
              )}
              <Button
                className="bg-blue-600 hover:bg-blue-700 ml-auto"
                onClick={handleSubmit}
                disabled={submitting || (!notes.trim() && responses.filter(r => r !== null).length === 0)}
                data-testid="submit-restroom-survey"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {i18n.language === 'fr' ? 'Envoi...' : 'Submitting...'}
                  </>
                ) : (
                  i18n.language === 'fr' ? 'Soumettre' : 'Submit'
                )}
              </Button>
            </div>
          </div>
        </GlassInteractionPanel>
      </BrandingShell>
    );
  }

  // Rating questions flow
  const question = surveyData?.questions?.[currentQuestion];
  const progress = ((currentQuestion + 1) / surveyData?.questions?.length) * 100;

  // Get translated question text
  const getQuestionText = (q) => {
    if (!q) return '';
    
    if (q.text && typeof q.text === 'object') {
      return i18n.language === 'fr' ? (q.text.fr || q.text.en) : (q.text.en || q.text.fr);
    }
    
    if (i18n.language === 'fr' && q.text_fr) {
      return q.text_fr;
    }
    
    return q.text || q.text_en || '';
  };

  return (
    <BrandingShell wallpaperUrl={branding?.wallpaper_url} fallbackGradient="from-slate-950 to-slate-900">
      {/* Language Toggle */}
      <div className="absolute top-4 right-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleLanguage}
          className="text-white/80 hover:text-white hover:bg-white/10"
        >
          <Globe className="w-4 h-4 mr-2" />
          {i18n.language === 'en' ? 'FR' : 'EN'}
        </Button>
      </div>

      <GlassInteractionPanel>
        <div className="p-6">
          {/* Header with Logo */}
          <div className="text-center mb-6">
            {branding.logo_url ? (
              <div className="flex justify-center mb-3">
                <img 
                  src={branding.logo_url.startsWith('http') ? branding.logo_url : `${API_BASE}${branding.logo_url}`}
                  alt={surveyData?.organization?.name || 'Logo'}
                  className={`object-contain ${logoSizeClasses[branding.logo_size] || logoSizeClasses.medium}`}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-3">
                <Droplets className="w-6 h-6 text-blue-600" />
              </div>
            )}
            <h1 className="text-xl font-bold text-white">{surveyData?.organization?.name}</h1>
            <p className="text-sm text-white/50">
              {i18n.language === 'fr' ? 'Avis' : 'Review'}
            </p>
          </div>

          {/* Progress */}
          <div className="mb-6">
            <div className="flex justify-between text-xs text-white/50 mb-1">
              <span>
                {i18n.language === 'fr' 
                  ? `Question ${currentQuestion + 1} sur ${surveyData?.questions?.length}`
                  : `Question ${currentQuestion + 1} of ${surveyData?.questions?.length}`}
              </span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div 
                className="h-full bg-cyan-500 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Question */}
          <div className="mb-8">
            <h2 className="text-lg font-medium text-white text-center mb-6">
              {getQuestionText(question)}
            </h2>

            {/* Star Rating Response */}
            <div className="flex justify-center space-x-2">
              {[1, 2, 3, 4, 5].map((rating) => (
                <button
                  key={rating}
                  onClick={() => handleRatingResponse(rating)}
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                    responses[currentQuestion]?.value === rating
                      ? 'bg-cyan-500 text-white scale-110'
                      : 'bg-white/5 text-white/30 hover:bg-white/10 hover:text-white/60'
                  }`}
                  data-testid={`rating-star-${rating}`}
                >
                  <Star className={`w-6 h-6 ${responses[currentQuestion]?.value >= rating ? 'fill-current' : ''}`} />
                </button>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
              disabled={currentQuestion === 0}
            >
              {i18n.language === 'fr' ? 'Retour' : 'Back'}
            </Button>
            
            {currentQuestion === surveyData?.questions?.length - 1 ? (
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => setShowNotes(true)}
                disabled={responses[currentQuestion] === null}
              >
                {i18n.language === 'fr' ? 'Suivant' : 'Next'}
              </Button>
            ) : (
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => setCurrentQuestion(currentQuestion + 1)}
                disabled={responses[currentQuestion] === null}
              >
                {i18n.language === 'fr' ? 'Suivant' : 'Next'}
              </Button>
            )}
          </div>
        </div>
      </GlassInteractionPanel>
    </BrandingShell>
  );
}
